import { Component, computed, effect, HostBinding, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin } from 'rxjs';
import { Shared } from '@shared/exports';
import { Accordion } from '../accordion/accordion';
import { DataTable, DataTableRow } from '../data-table/data-table';
import { ProspectoSummaryService } from '../../services/prospecto-summary.service';
import {
  BitacoraPrivacidad,
  CanalesComunicacionResponse,
  DatosProspecto,
  HistorialAdjunto,
  HistorialDemo,
  HistorialValuacion,
  HistorialVenta,
  Seguimiento,
  TableroIcon
} from '../../interfaces/prospecto-summary.interfaces';
import { HomeRechazarProspecto } from 'src/app/modules/dashboards/home/components/home-rechazar-prospecto/home-rechazar-prospecto';
import { Modals } from '@services/modals/modals';
import { HomeReprogramarSeguimientos } from 'src/app/modules/dashboards/home/components/home-reprogramar-seguimientos/home-reprogramar-seguimientos';
import { HomeDesbloqueoProspecto } from 'src/app/modules/dashboards/home/components/home-desbloqueo-prospecto/home-desbloqueo-prospecto';
import { IframeModal } from '../iframe-modal/iframe-modal';

type AccordionType = 'datos-prospecto' | 'datos-comunicacion' | 'oportunidad-venta' | 'resumen-seguimientos' |
  'bitacora-privacidada' | 'historial-ventas' | 'historial-valuaciones' | 'historial-demos' | 'historial-adjuntos' | null;

@Component({
  selector: 'app-prospecto-summary',
  standalone: true,
  imports: [CommonModule, Shared, Accordion, DataTable],
  templateUrl: './prospecto-summary.html',
  styleUrl: './prospecto-summary.scss'
})
export class ProspectoSummary {
  private readonly service = inject(ProspectoSummaryService);
  private readonly dialogs = inject(Modals);

  // Inputs mínimos requeridos
  idDistribuidor = input.required<string>();
  idProspecto = input.required<string>();
  idEjecutivo = input.required<string>();
  dominio = input<string>('https://qa2.sicopweb.com/8.0/workspace/');
  show = input<boolean>(true);
  nombreProspecto = input<string>('');
  tipoActividad = input<string>('');
  idSeguimiento = input<string>('');
  style = input<'classic' | 'new'>('classic');
  mostrarNuevoModal = input<boolean>(false);

  @HostBinding('class.style-new') get isStyleNew() { return this.style() === 'new'; }

  // Outputs
  cumplirSeguimiento = output<Seguimiento>();
  datosLoaded = output<DatosProspecto>();
  iconClick = output<TableroIcon>();

  // Estado de carga
  loading = signal<boolean>(false);
  isEnvironmentReady = signal<boolean>(false);

  // Datos del prospecto
  datosProspecto = signal<DatosProspecto | null>(null);
  bitacoraPrivacidad = signal<BitacoraPrivacidad[]>([]);
  historialDemos = signal<HistorialDemo[]>([]);
  historialVentas = signal<HistorialVenta[]>([]);
  historialValuaciones = signal<HistorialValuacion[]>([]);
  historialAdjuntos = signal<HistorialAdjunto[]>([]);
  tableroIcons = signal<TableroIcon[]>([]);
  loadingIcons = signal<boolean>(false);
  canalesComunicacion = signal<CanalesComunicacionResponse | null>(null);

  // Estado de acordeones
  openAccordion = signal<AccordionType>(null);
  demoIndex = signal<number>(0);
  ventaIndex = signal<number>(0);
  valuacionIndex = signal<number>(0);
  adjuntoIndex = signal<number>(0);


  // Nombre a mostrar (computed)
  displayName = computed(() => {
    const datos = this.datosProspecto();
    if (datos?.nombreCompleto) return datos.nombreCompleto;
    if (this.nombreProspecto()) return this.nombreProspecto();
    return 'Sin nombre';
  });

  // Host bindings para controlar visibilidad
  @HostBinding('style.width')
  get hostWidth(): string { return this.show() ? '380px' : '0'; }

  @HostBinding('style.overflow')
  get hostOverflow(): string { return this.show() ? 'visible' : 'hidden'; }

  // Método para click en icono
  onIconClick(icon: TableroIcon): void {

    const nombreCompleto = icon.nombreCompleto || this.nombreProspecto() || '';
    const href = icon.href || '';
    const validar = icon.validar ?? false;
    const tipo = icon.tipo ?? '';
    const idProspecto = icon.idProspecto || this.datosProspecto()?.idProspecto || '';

    if (icon.requestType === 'OpenDesktopPage' && href) {
      this.openDesktopPage(icon);
    } else if (icon.requestType === 'OpenModalDialog' || icon.requestType === 'OpenModalDialogCanales') {
      switch (tipo) {
        case 'RP': {
          const params = new URLSearchParams(href.split('?')[1] || '');
          const idSeguimiento = params.get('IdSeguimiento');
          this.abrirDialogReprogramar(idProspecto, idSeguimiento || '');
          break;
        }
        case 'DESBLOQPROSPECTOBDC':
        case 'DESBLOQPROSPECTO':
          this.abrirDialogDesbloqueo(idProspecto, '');
          break;
        case 'R':
          this.abrirDialogRechazo(idProspecto, nombreCompleto, href);
          break;
        default:
          break;
      }
    }
    this.iconClick.emit(icon);
  }

  private openDesktopPage(icon: TableroIcon): void {
    const nombreCompleto = this.datosProspecto()?.nombreCompleto || '';
    let dom = this.dominio();

    // Normalizar dominio
    if (dom) {
      if (dom.startsWith('http://')) {
        dom = dom.replace(/^http:\/\//i, 'https://');
      }
      if (dom.endsWith('/index.html')) {
        dom = dom.replace(/\/index\.html$/, '');
      } else if (dom.endsWith('index.html')) {
        dom = dom.replace(/index\.html$/, '').replace(/\/$/, '');
      }
    }

    if (!dom) {
      console.error('Dominio no disponible');
      return;
    }

    const message = {
      type: 'openLegacyTab',
      tabName: nombreCompleto,
      url: `${dom}/${icon.href}`
    };

    console.log('postMessage openDesktopPage:', message);
    window.parent.postMessage(message, dom);
  }

  constructor() {
    // Effect para cargar datos cuando cambian los inputs
    effect(() => {
      const idDist = this.idDistribuidor();
      const idProsp = this.idProspecto();
      const idEjec = this.idEjecutivo();

      if (idDist && idProsp && idEjec) {
        this.initAndLoadData(idDist, idProsp, idEjec);
      }
    });
  }

  private async initAndLoadData(idDistribuidor: string, idProspecto: string, idEjecutivo: string): Promise<void> {
    // Primero inicializar el ambiente si no está listo
    if (!this.isEnvironmentReady()) {
      try {
        await this.service.loadEnvironment(idDistribuidor);
        this.isEnvironmentReady.set(true);
      } catch (error) {
        console.error('Error inicializando ambiente:', error);
        return;
      }
    }

    this.cargarDatosProspecto(idDistribuidor, idProspecto);
    this.cargarTableroIcons(idDistribuidor, idEjecutivo, idProspecto, this.idSeguimiento());
    this.cargarCanalesComunicacion(idDistribuidor, idEjecutivo, idProspecto);
  }

  private cargarCanalesComunicacion(idDistribuidor: string, idEjecutivo: string, idProspecto: string): void {
    this.canalesComunicacion.set(null);
    this.service.getCanalesComunicacion(idDistribuidor, idEjecutivo, idProspecto).subscribe({
      next: (resp: any) => {
        console.log('[canales-comunicacion] respuesta API:', resp);
        if (resp?.success && resp?.response) {
          /* console.log('[canales-comunicacion] token recibido:', resp.response.token);
          console.log('[canales-comunicacion] canales recibidos:', resp.response.canales); */
          this.canalesComunicacion.set(resp.response);
        }
      },
      error: (err: any) => {
        console.error('Error cargando canales de comunicación:', err);
      }
    });
  }

  private cargarTableroIcons(idDistribuidor: string, idEjecutivo: string, idProspecto: string, idSeguimiento: string = ''): void {
    this.loadingIcons.set(true);
    this.tableroIcons.set([]);

    this.service.getTableroIcon(idDistribuidor, idEjecutivo, idProspecto, idSeguimiento).subscribe({
      next: (resp: any) => {
        if (resp?.success && Array.isArray(resp?.response?.icons)) {
          console.log('getTableroIcon', resp);
          this.tableroIcons.set(resp.response.icons);
        }
        this.loadingIcons.set(false);
      },
      error: (err: any) => {
        console.error('Error cargando iconos del tablero:', err);
        this.loadingIcons.set(false);
      }
    });
  }

  private cargarDatosProspecto(idDistribuidor: string, idProspecto: string): void {
    this.loading.set(true);
    this.resetData();
    console.log('cargarDatosProspecto');
    console.log('idDistribuidor', idDistribuidor);
    console.log('idProspecto', idProspecto);
    this.service.getDatosProspecto(idDistribuidor, idProspecto).subscribe({
      next: (resp: any) => {
        if (!resp?.success || !resp?.datos?.prospecto) {
          console.error('Respuesta inválida de getDatosProspecto');
          this.loading.set(false);
          return;
        }

        console.log('DatosProspecto', resp);

        const p = resp.datos.prospecto;
        const datos: DatosProspecto = {
          idProspecto: p.idProspecto,
          nombreCompleto: p.nombreCompleto,
          nombre: p.nombre,
          segundoNombre: p.segundoNombre,
          paterno: p.paterno,
          genero: p.genero,
          fechaNacimiento: p.fechaNacimiento,
          telefono1: p.telefono1,
          oficina1: p.oficina1,
          eMail: p.eMail,
          tipoPersona: p.TipoPersona,
          observaciones: p.observaciones,
          primerContacto: p.primerContacto,
          registro: p.registro,
          idPropietario: p.idPropietario,
          propietario: p.propietario,
          idFuente: p.idFuente,
          fuente: p.Fuente,
          subcampana: p.Subcampana,
          idSubcampana: p.idSubcampana,
          status: p.Status,
          subStatus: p.SubStatus,
          tipoPrimerContacto: p.TipoPrimerContacto,
          tipoDeCierre: p.TipoDeCierre,
          tipoDeCompra: p.TipoDeCompra,
          tipoDeVenta: p.TipoDeVenta,
          estadoCivil: p.EstadoCivil,
          idOrigen: p.idOrigen,
          distribuidorOrigen: p.distribuidorOrigen,
          idDistribuidorDigital: p.idDistribuidorDigital,
          distribuidorDigital: p.distribuidorDigital,
          idAuto: p.idAuto,
          autoDeInteres: p.autoDeInteres,
          tipoNegocio: p.tipoNegocio,
          tiempoDeEspera: p.tiempoDeEspera,
          tiempoDeAtencion: p.tiempoDeAtencion,
          privacidad: p.privacidad,
          bloqueado: p.bloqueado,
          duplicado: p.duplicado,
          idCompartido: p.idCompartido,
          contactoInicio: p.contactoInicio,
          contactoFin: p.contactoFin,
          seguimientos: resp.datos.seguimientos || [],
          productosInteres: resp.datos.productosInteres || [],
          lead: p.lead || '',
          temperatura: p.temperatura || '0%',
          inactivo: p.inactivo || false,
          visitas: p.visitas || '',
          tipoToma: p.tipoToma || '',
        };

        this.datosProspecto.set(datos);
        this.datosLoaded.emit(datos);
        this.loading.set(false);

        // Cargar datos adicionales
        const idDistDigital = datos.idDistribuidorDigital || idDistribuidor;
        this.cargarDatosAdicionales(idDistribuidor, idDistDigital, idProspecto);
      },
      error: (err: any) => {
        console.error('Error cargando datos del prospecto:', err);
        this.loading.set(false);
      }
    });
  }

  private cargarDatosAdicionales(idDistribuidor: string, idDistDigital: string, idProspecto: string): void {
    forkJoin({
      bitacora: this.service.getBitacoraPrivacidad(idDistribuidor, idProspecto),
      demos: this.service.getHistorialDemos(idDistDigital, idProspecto),
      ventas: this.service.getHistorialVentas(idDistDigital, idProspecto),
      valuaciones: this.service.getHistorialValuaciones(idDistDigital, idProspecto),
      adjuntos: this.service.getHistorialAdjuntos(idDistDigital, idProspecto)
    }).subscribe({
      next: (resp) => {
        if (resp.bitacora?.success && Array.isArray(resp.bitacora?.Bitacora)) {
          this.bitacoraPrivacidad.set(resp.bitacora.Bitacora);
        }
        if (resp.demos?.success && Array.isArray(resp.demos?.Demos)) {
          this.historialDemos.set(resp.demos.Demos);
        }
        if (resp.ventas?.success && Array.isArray(resp.ventas?.ventas)) {
          this.historialVentas.set(resp.ventas.ventas);
        }
        if (resp.valuaciones?.success && Array.isArray(resp.valuaciones?.Valuaciones)) {
          this.historialValuaciones.set(resp.valuaciones.Valuaciones);
        }
        if (resp.adjuntos?.success && Array.isArray(resp.adjuntos?.Datos)) {
          this.historialAdjuntos.set(resp.adjuntos.Datos);
        }
      },
      error: (err) => {
        console.error('Error cargando datos adicionales:', err);
      }
    });
  }

  private resetData(): void {
    this.datosProspecto.set(null);
    this.bitacoraPrivacidad.set([]);
    this.historialDemos.set([]);
    this.historialVentas.set([]);
    this.historialValuaciones.set([]);
    this.historialAdjuntos.set([]);
    this.canalesComunicacion.set(null);
    this.demoIndex.set(0);
    this.ventaIndex.set(0);
    this.valuacionIndex.set(0);
    this.adjuntoIndex.set(0);
  }

  // Métodos para recargar datos manualmente
  reload(): void {
    const idDist = this.idDistribuidor();
    const idProsp = this.idProspecto();
    if (idDist && idProsp) {
      this.cargarDatosProspecto(idDist, idProsp);
    }
  }

  // ============ ACORDEONES ============

  toggleAccordion(id: AccordionType): void {
    if (this.openAccordion() === id) {
      this.openAccordion.set(null);
    } else {
      this.openAccordion.set(id);
    }
  }

  isAccordionOpen(id: AccordionType): boolean {
    return this.openAccordion() === id;
  }

  // ============ DATOS DEL PROSPECTO ============

  getDatosProspectoRows(): DataTableRow[] {
    const datos = this.datosProspecto();
    if (!datos) return [];

    return [
      { label: 'Clave', value: datos.idProspecto },
      { label: 'Distribuidor', value: datos.distribuidorDigital },
      { label: 'Ejecutivo', value: datos.propietario },
      { label: 'Estatus', value: datos.status },
      { label: 'Tipo de primer contacto', value: datos.tipoPrimerContacto },
      { label: 'Fuente de información', value: datos.fuente },
      { label: 'Subcampaña', value: datos.subcampana || '(SIN DEFINIR)' },
      { label: 'Primer contacto', value: this.formatDate(datos.primerContacto) },
      { label: 'Prospecto Lead', value: datos.lead ? 'SI' : 'NO' },
      { label: 'Temperatura', value: datos.temperatura || '0%' },
      { label: 'Visitas', value: datos.visitas || '0' },
      { label: 'Tipo Toma', value: datos.tipoToma || '' },
      { label: '¿Está inactivo?', value: datos.inactivo ? 'SI' : 'NO' },
      { label: 'Registro', value: this.formatDate(datos.registro) },
      { label: 'Observaciones', value: this.formatDate(datos.observaciones) }
    ];
  }

  getDatosComunicacionRows(): DataTableRow[] {
    const datos = this.datosProspecto();
    if (!datos) return [];

    const rows: DataTableRow[] = [];

    const inicio = datos.contactoInicio || '';
    const fin = datos.contactoFin || '';
    const esHorarioValido = !((inicio === '00:00' && fin === '00:00') || (inicio === '' && fin === ''));

    if (esHorarioValido) {
      rows.push({ label: 'Horario de contacto', value: `${inicio}hrs a ${fin}hrs` });
    }

    rows.push(
      { label: 'Teléfono', value: datos?.telefono1 || datos?.oficina1 || '-' },
      { label: 'Email', value: datos.eMail }
    );

    return rows;
  }

  getCanalesHabilitados(): Array<{ tipo: string; icon: string; etiqueta: string; valor: string; urlAccion: string }> {
    const data = this.canalesComunicacion();
    if (!data) return [];

    const iconByTipo: Record<string, string> = {
      email: 'email-multiple',
      llamada: 'phone-outgoing',
      whatsapp: 'mdi-whatsapp',
      sms: 'cellphone-message'
    };

    const labelByTipo: Record<string, string> = {
      email: 'Correo',
      llamada: 'Llamada',
      whatsapp: 'WhatsApp',
      sms: 'SMS'
    };

    const habilitadoByTipo: Record<string, boolean> = {
      email: !!data.habilitarCorreo,
      llamada: !!data.habilitarTelefonia,
      whatsapp: !!data.habilitarWhatsapp,
      sms: !!data.habilitarSms
    };

    const token = data.token || '';
    const canales = data.canales || [];
    return canales
      .filter(c => habilitadoByTipo[c.tipo])
      .map(c => {
        const urlOriginal = c.urlAccion;
        const urlConToken = this.appendToken(urlOriginal, token);
        /*  console.log(`[canal ${c.tipo}] urlOriginal:`, urlOriginal);
         console.log(`[canal ${c.tipo}] token:`, token);
         console.log(`[canal ${c.tipo}] urlFinal:`, urlConToken); */
        return {
          tipo: c.tipo,
          icon: iconByTipo[c.tipo] || 'chat',
          etiqueta: labelByTipo[c.tipo] || c.etiqueta || c.tipo,
          valor: c.valor,
          urlAccion: urlConToken
        };
      });
  }

  private appendToken(url: string, token: string): string {
    if (!url || !token) return url;
    if (/[?&]Token=/i.test(url)) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}Token=${encodeURIComponent(token)}`;
  }

  onCanalClick(urlAccion: string): void {
    let dom = this.dominio();

    if (dom) {
      if (dom.startsWith('http://')) {
        dom = dom.replace(/^http:\/\//i, 'https://');
      }
      if (dom.endsWith('/index.html')) {
        dom = dom.replace(/\/index\.html$/, '');
      } else if (dom.endsWith('index.html')) {
        dom = dom.replace(/index\.html$/, '').replace(/\/$/, '');
      }
    }

    if (!dom) {
      console.error('Dominio no disponible');
      return;
    }

    const message = {
      type: 'openLegacyTab',
      tabName: 'Multicanal',
      url: urlAccion
    };

    console.log('========================================');
    console.log('[onCanalClick] URL completa que se manda:');
    console.log(urlAccion);
    console.log('[onCanalClick] postMessage payload:', message);
    console.log('[onCanalClick] target dominio:', dom);
    console.log('========================================');
    window.parent.postMessage(message, dom);
  }

  getOportunidadVentaRows(): DataTableRow[] {
    const datos = this.datosProspecto();
    if (!datos) return [];

    const producto = datos.productosInteres?.[0];
    return [
      { label: 'Auto de interés', value: producto?.auto || datos.autoDeInteres || '-' },
      { label: 'Marca', value: producto?.marca || '-' },
      { label: 'Versión', value: producto?.version || '-' }
    ];
  }

  formatDate(dateStr?: string): string {
    if (!dateStr) return '-';
    const date = dateStr.split(' ')[0];
    return date || '-';
  }

  // ============ SEGUIMIENTOS ============

  getSeguimientos(): Seguimiento[] {
    return this.datosProspecto()?.seguimientos || [];
  }

  isSeguimientoCumplido(seguimiento: Seguimiento): boolean {
    return !seguimiento.cumplida?.startsWith('1900-01-01');
  }

  onCumplirSeguimiento(seguimiento: Seguimiento): void {
    const idSeguimiento = seguimiento.idSeguimiento;
    const idProspecto = this.datosProspecto()?.idProspecto || '';
    const nombreCompleto = this.datosProspecto()?.nombreCompleto || '';
    let dom = this.dominio();

    // Normalizar dominio
    if (dom) {
      if (dom.startsWith('http://')) {
        dom = dom.replace(/^http:\/\//i, 'https://');
      }
      if (dom.endsWith('/index.html')) {
        dom = dom.replace(/\/index\.html$/, '');
      } else if (dom.endsWith('index.html')) {
        dom = dom.replace(/index\.html$/, '').replace(/\/$/, '');
      }
    }

    if (!dom) {
      console.error('Dominio no disponible');
      return;
    }

    const message = {
      type: 'openLegacyTab',
      tabName: nombreCompleto,
      url: `${dom}/multi/seguimientos.html?PantallaCumplimiento=1&IdSeguimiento=${idSeguimiento}&IdProspecto=${idProspecto}`
    };

    console.log('postMessage cumplir seguimiento:', message);
    window.parent.postMessage(message, dom);
  }

  // ============ BITÁCORA DE PRIVACIDAD ============

  formatDateTime(dateStr?: string): string {
    if (!dateStr) return '-';
    const parts = dateStr.split(' ');
    if (parts.length < 2) return dateStr;

    const dateParts = parts[0].split('-');
    if (dateParts.length !== 3) return dateStr;

    const timeParts = parts[1].split(':');
    const time = timeParts.length >= 2 ? `${timeParts[0]}:${timeParts[1]}` : '';

    return `${dateParts[2]}-${dateParts[1]}-${dateParts[0]} ${time}`;
  }

  // ============ HISTORIAL DE DEMOS ============

  getCurrentDemo(): HistorialDemo | null {
    const demos = this.historialDemos();
    const index = this.demoIndex();
    return demos[index] || null;
  }

  getCurrentDemoRows(): DataTableRow[] {
    const demo = this.getCurrentDemo();
    if (!demo) return [];

    return [
      { label: 'VIN', value: demo.VIN },
      { label: 'Auto', value: demo.Auto },
      { label: 'Ejecutivo', value: demo.Ejecutivo },
      { label: 'Fecha', value: this.formatDateTime(demo.Fecha) },
      { label: 'Estatus', value: demo.Status },
      { label: 'Observaciones', value: demo.Observaciones || '-' }
    ];
  }

  prevDemo(): void {
    const current = this.demoIndex();
    if (current > 0) {
      this.demoIndex.set(current - 1);
    }
  }

  nextDemo(): void {
    const current = this.demoIndex();
    const total = this.historialDemos().length;
    if (current < total - 1) {
      this.demoIndex.set(current + 1);
    }
  }

  getTotalDemos(): number {
    return this.historialDemos().length;
  }

  // ============ HISTORIAL DE VENTAS ============

  getCurrentVenta(): HistorialVenta | null {
    const ventas = this.historialVentas();
    const index = this.ventaIndex();
    return ventas[index] || null;
  }

  getCurrentVentaRows(): DataTableRow[] {
    const venta = this.getCurrentVenta();
    if (!venta) return [];

    return [
      { label: 'Id Venta', value: venta.IdVenta },
      { label: 'Fecha Entrega Física', value: this.formatDateTime(venta.FechaEntregaFisica) },
      { label: 'Fecha Reporte Ventas', value: this.formatDateTime(venta.FechaReporteVentas) },
      { label: 'Fecha Facturación', value: this.formatDateTime(venta.FechaFacturacion) },
      { label: 'VIN', value: venta.VIN },
      { label: 'Auto', value: `${venta.Auto} ${venta.Modelo || ''}`.trim() },
      { label: 'Versión', value: venta.Version || '-' },
      { label: 'Placas', value: venta.Placas || 'N/A' },
      { label: 'Forma de Pago', value: venta.FormaPago || '-' },
      { label: 'Garantía', value: venta.Garantia || '-' },
      { label: 'Seguro', value: venta.Seguro || '-' }
    ];
  }

  prevVenta(): void {
    const current = this.ventaIndex();
    if (current > 0) {
      this.ventaIndex.set(current - 1);
    }
  }

  nextVenta(): void {
    const current = this.ventaIndex();
    const total = this.historialVentas().length;
    if (current < total - 1) {
      this.ventaIndex.set(current + 1);
    }
  }

  getTotalVentas(): number {
    return this.historialVentas().length;
  }

  // ============ HISTORIAL DE VALUACIONES ============

  getCurrentValuacion(): HistorialValuacion | null {
    const valuaciones = this.historialValuaciones();
    const index = this.valuacionIndex();
    return valuaciones[index] || null;
  }

  getCurrentValuacionRows(): DataTableRow[] {
    const valuacion = this.getCurrentValuacion();
    if (!valuacion) return [];

    return [
      { label: 'Clave', value: valuacion.IdProspecto },
      { label: 'Fecha Valuación', value: this.formatDateTime(valuacion.FechaValuacion) },
      { label: 'Estatus', value: valuacion.Status || '-' },
      { label: 'Distribuidor Valuación', value: valuacion.DistribuidorValuacion || '-' },
      { label: 'Valuador', value: valuacion.Valuador || '-' },
      { label: 'Auto', value: valuacion.Auto || '-' },
      { label: 'Placas', value: valuacion.Placas || '-' }
    ];
  }

  prevValuacion(): void {
    const current = this.valuacionIndex();
    if (current > 0) {
      this.valuacionIndex.set(current - 1);
    }
  }

  nextValuacion(): void {
    const current = this.valuacionIndex();
    const total = this.historialValuaciones().length;
    if (current < total - 1) {
      this.valuacionIndex.set(current + 1);
    }
  }

  getTotalValuaciones(): number {
    return this.historialValuaciones().length;
  }

  // ============ HISTORIAL DE ADJUNTOS ============

  getCurrentAdjunto(): HistorialAdjunto | null {
    const adjuntos = this.historialAdjuntos();
    const index = this.adjuntoIndex();
    return adjuntos[index] || null;
  }

  getCurrentAdjuntoRows(): DataTableRow[] {
    const adjunto = this.getCurrentAdjunto();
    if (!adjunto) return [];

    return [
      { label: 'Nombre', value: adjunto.Nombre, link: adjunto.Url },
      { label: 'Tamaño', value: adjunto.Tamano || '-' }
    ];
  }

  prevAdjunto(): void {
    const current = this.adjuntoIndex();
    if (current > 0) {
      this.adjuntoIndex.set(current - 1);
    }
  }

  nextAdjunto(): void {
    const current = this.adjuntoIndex();
    const total = this.historialAdjuntos().length;
    if (current < total - 1) {
      this.adjuntoIndex.set(current + 1);
    }
  }

  getTotalAdjuntos(): number {
    return this.historialAdjuntos().length;
  }

  abrirDialogRechazo(idProspecto: string, nombreCompleto: string, href: string = ''): void {
    if (this.mostrarNuevoModal()) {
      this.dialogs.open(HomeRechazarProspecto, {
        title: 'Rechazar Prospecto',
        titleIcon: 'close-circle-outline',
        data: {
          idDistribuidor: this.idDistribuidor(),
          idEjecutivo: this.idEjecutivo(),
          idProspecto: idProspecto || '',
          idMarca: this.idDistribuidor().slice(0, 3),
          nombreCompleto: nombreCompleto || ''
        },
        width: 'min(600px, 90vw)',
        height: 'auto',
        maxHeight: '90vw',
        easyClose: true
      }).closed.then((result: any) => { });
      return;
    }

    this.abrirIframeModal('Rechazar Prospecto', 'close-circle-outline', href);
  }

  private abrirIframeModal(title: string, titleIcon: string, href: string): void {
    let dom = this.dominio();

    if (dom) {
      if (dom.startsWith('http://')) {
        dom = dom.replace(/^http:\/\//i, 'https://');
      }
      if (dom.endsWith('/index.html')) {
        dom = dom.replace(/\/index\.html$/, '');
      } else if (dom.endsWith('index.html')) {
        dom = dom.replace(/index\.html$/, '').replace(/\/$/, '');
      }
    }

    if (!dom) {
      console.error('Dominio no disponible');
      return;
    }

    const baseUrl = `${dom}/${href}`;
    const separator = baseUrl.includes('?') ? '&' : '?';
    const url = /[?&]isAngular=/i.test(baseUrl) ? baseUrl : `${baseUrl}${separator}isAngular=1`;
    console.log('Url FrameModal ', url);
    this.dialogs.open(IframeModal, {
      title,
      titleIcon,
      data: { url },
      width: 'min(1100px, 95vw)',
      height: '95vh',
      maxHeight: '95vh',
      easyClose: true
    }).closed.then((result: any) => {
      if (result) {
        this.reload();
      }
    });
  }


  abrirDialogReprogramar(idProspecto: string, idSeguimiento: string): void {
    this.dialogs.open(HomeReprogramarSeguimientos, {
      title: 'Reprogramar Seguimiento',
      titleIcon: 'calendar-clock-outline',
      data: {
        idDistribuidor: this.idDistribuidor(),
        idPropietario: this.idEjecutivo(),
        idSeguimiento: idSeguimiento || '',
      },
      width: 'min(900px, 90vw)',
      height: 'auto',
      maxHeight: '90vw',
      panelClass: 'conversation-dialog-responsive',
      easyClose: true
    }).closed.then((result: any) => { });
  }

  abrirDialogDesbloqueo(idProspecto: string, idSeguimiento: string): void {
    this.dialogs.open(HomeDesbloqueoProspecto, {
      title: 'Desbloquear Prospecto',
      titleIcon: 'account-lock-open-outline',
      data: {
        idDistribuidor: this.idDistribuidor,
        idEjecutivo: this.idEjecutivo,
        idProspecto: idProspecto || '',
      },
      width: 'min(600px, 90vw)',
      height: 'auto',
      maxHeight: '90vw',
      panelClass: 'conversation-dialog-responsive',
      easyClose: true
    }).closed.then((result: any) => { });
  }

}
